import {
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  Output,
  QueryList,
} from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';

import { GasColor } from '../../../core/colors/color.namespace';
import { GasIcon } from '../../../utils/gas-icons/icon/gas-icon.namespace';
import { GasStepsComponent } from '../steps/gas-steps.component';
import { GasStepsState, GasStepsType } from '../gas-steps.interface';

const CORRECT_LENGTH = 1;

@Component({
  selector: 'gas-vertical-steps',
  templateUrl: './vertical-steps.component.html',
  styleUrls: ['./vertical-steps.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GasVerticalStepsComponent {
  /** Шаги */
  @ContentChildren(GasStepsComponent)
  public steps: QueryList<GasStepsComponent>;

  @Output()
  public onNextStep: EventEmitter<boolean> = new EventEmitter(false);

  @Output('onSelectedStep')
  public onSelectedStep$ = new EventEmitter<GasStepsComponent>();

  @Output('onEditStep')
  public onEditStep$ = new EventEmitter<GasStepsComponent>();

  public baseStep$: Observable<string>;

  private activeStepSubject$ = new BehaviorSubject<GasStepsType>(GasStepsState.Active);
  private baseStepSubject$ = new BehaviorSubject<GasStepsType>(GasStepsState.Base);

  constructor() {
    this.baseStep$ = this.baseStepSubject$.asObservable();
  }

  public visibilityStep(step: GasStepsComponent): boolean {
    return step.isViewContent || step.status === GasStepsState.Success && step.visibility;
  }

  /** Устанавливаем цвет вертикальной линии */
  public getBackgroundColor(status: string): string {
    return status === GasIcon.Notifications.Success ?
      GasColor.Color.SuccessDark :
      GasColor.Color.Disabled;
  }

  /** Добавляем вертикальную и горизонтальную линии если шаг не последний */
  public isLine(index: number): boolean {
    return index < this.steps.length - CORRECT_LENGTH;
  }

  public isPrimaryButton(step: GasStepsComponent): Observable<boolean> {
    return of(!step.isViewContent && this.activeStepSubject$.getValue() === step.status);
  }

  public selectedStep(step: GasStepsComponent): void {
    this.setViewContent(step);
    this.onSelectedStep$.emit(step);
  }

  public editStep(step: GasStepsComponent): void {
    this.setViewContent(step);
    this.onEditStep$.emit(step);
  }

  /** Переходим к следующему шагу */
  public nextStep(step: GasStepsComponent, index: number): void {
    this.hideContentActiveStap(step);
    step.viewNextButton = false;
    step.status = GasStepsState.Success;
    const nextStep = this.steps.toArray()[index + 1];

    if (this.canMoveNextStep(index)) {
      nextStep.status = nextStep.status === GasStepsState.Base ? GasStepsState.Active : nextStep.status;
      this.onNextStep.emit(false);
    } else {
      this.onNextStep.emit(true);
    }
  }

  /** Скрываем контент всех шагов */
  private hideContentActiveStap(step: GasStepsComponent): void {
    step.hideContent();
  }

  /** Проверяем актуальность следующего шага */
  private canMoveNextStep(index: number): boolean {
    const actualSteps = this.steps.filter((step: GasStepsComponent) => !step.successStep);

    return this.steps.toArray()[index + 1] &&
      !!actualSteps.length;
  }

  private setViewContent(step: GasStepsComponent): void {
    this.hideContentActiveStap(step);
    step.isViewContent = true;
    step.viewNextButton = true;
  }
}
